import { useEffect, useState } from 'react'
import { Card } from 'primereact/card'
import { Column as TableColumn } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Tag } from 'primereact/tag'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { BoltChangePhysicalPropertiesDto } from '../../_generated/api/tagged-hardware'
import BoundaryIndicator from '../common/pure/BoundaryIndicator'
import DateDisplay from '../common/pure/DateDisplay'
import CreateManualMeasurementDialog from '../dialogs/CreateManualMeasurementDialog'
import { Column, Row } from '../common/pure/FlexContainer'
import { DropdownField, NumberField } from '../common/pure/input-fields'
import { ContentColumn, LayoutRowContainer } from '../common/pure/layout'
import { getMenuItemExport } from '../common/menu-items'
import MeasurementCommentDialog from '../dialogs/MeasurementCommentDialog'
import MeasurementDetailsDialog from '../dialogs/MeasurementDetailsDialog'
import PrimeIcon, { pi } from '../common/pure/PrimeIcon'
import StructureScreen from './StructureScreen'
import WaveGraph from '../graph/WaveGraph'
import ExpandableTable from '../table/ExpandableTable'
import {
    ArchiveButton,
    DuplicateButton,
    EditButton,
    ExportButton,
    RestoreButton,
    TableActionContainer,
} from '../table/TableButton'
import CardContextMenu from '../common/CardContextMenu'
import { useBoltContext } from '../contexts/BoltContext'
import { useExportContext } from '../contexts/ExportContext'
import { useUserContext } from '../contexts/UserContext'
import Measurement from '../../models/Measurement'
import MenuBuilder from '../../tools/MenuBuilder'
import { BoltDiameterSettings, BoltTypeSettings, MaterialSettings } from '../../tools/settings'
import { TooltipAvatar } from '../common/ProfileAvatar'
import { toDecimalPlaces, degreeC, mm } from '../../tools/format'
import t, { _ta } from '../../tools/t'
import FullScreenLoading from '../common/FullScreenLoading'
import ClassNameBuilder from '../../tools/ClassNameBuilder'
import { useArchiveContext } from '../contexts/ArchiveContext'
import { ErrorMessageScreen } from '../common/pure/MessageScreen'
import TooltipWrapper from '../common/pure/TooltipWrapper'

export default function BoltScreen() {
    const { me, can } = useUserContext()
    const { exportBolt, exportMeasurement } = useExportContext()
    const {
        loading,
        selectedBolt,
        selectedBoltParent,
        updateBolt,
        restoreBolt,
        selectedMeasurement,
        setSelectedMeasurement,
        measurementSensor,
        measurementDevice,
        archiveMeasurement,
        restoreMeasurement,
        createManualMeasurement,
        updateMeasurementComment,
    } = useBoltContext()
    const { includeArchived } = useArchiveContext()
    const [editableData, setEditableData] = useState<BoltChangePhysicalPropertiesDto>()
    const [hasChanged, setHasChanged] = useState(false)
    const [createManualVisible, setCreateManualVisible] = useState(false)
    const [clonableMeasurement, setClonableMeasurement] = useState<Measurement>()
    const [commentableMeasurement, setCommentableMeasurement] = useState<Measurement>()
    const canWriteHardware = can(PermissionSuffix.HardwareWrite)

    const isArchived = !!selectedBolt?.isArchived || !!selectedBoltParent?.isArchived

    useEffect(() => {
        const physicalProperties = selectedBolt?.asPhysicalProperties()
        if (physicalProperties) setEditableData(physicalProperties)
    }, [selectedBolt])

    useEffect(() => {
        if (!isArchived) return
        setCreateManualVisible(false)
        setClonableMeasurement(undefined)
        setCommentableMeasurement(undefined)
    }, [isArchived])

    if (!selectedBolt) {
        if (loading) return <FullScreenLoading />
        return <ErrorMessageScreen />
    }

    const { activeReference, latestElongation } = selectedBolt

    const changeData = (change: Partial<BoltChangePhysicalPropertiesDto>) => {
        if (isArchived || !editableData) return
        setEditableData({ ...editableData, ...change })
        setHasChanged(true)
    }

    const submit = async () => {
        if (isArchived || !editableData) return
        const success = await updateBolt(selectedBolt, editableData)
        if (success) setHasChanged(false)
    }

    const onExportMeasurement = (measurement?: Measurement) =>
        exportMeasurement(selectedBolt?.id, measurement?.id, true)

    const submitCommentChange = (comment: string) => {
        if (
            !canWriteHardware ||
            isArchived ||
            !commentableMeasurement ||
            commentableMeasurement.isArchived
        )
            return
        updateMeasurementComment(selectedBolt, commentableMeasurement, comment)
    }

    const ancestors = selectedBoltParent?.ancestors ?? []
    ancestors.push(selectedBolt.asAncestor())

    const menuItems = new MenuBuilder()
        .addIf(
            {
                label: t('manual_measurement_create'),
                icon: pi('plus'),
                command: () => setCreateManualVisible(true),
            },
            canWriteHardware && !isArchived
        )
        .toArray()

    // Because measurements of a bolt are not contained in a standalone request, we must filter archived state locally.
    const measurements = selectedBolt.measurements.filter((m) => includeArchived || !m.isArchived)
    measurements.sort((a, b) => (a.measuredAt?.getTime() ?? 0) - (b?.measuredAt?.getTime() ?? 0))

    return (
        <StructureScreen
            node={selectedBolt}
            parent={selectedBoltParent}
            ancestors={ancestors}
            nodeHasChanged={hasChanged}
            onSaveChanges={canWriteHardware ? submit : undefined}
            onRestore={canWriteHardware ? () => restoreBolt(selectedBolt.id) : undefined}
            menu={[getMenuItemExport(() => exportBolt(selectedBolt.id), t('bolt_export'))]}
        >
            <LayoutRowContainer>
                <ContentColumn columns={1}>
                    <Card title={t('common_details')}>
                        <Column gap={0.75}>
                            <NumberField
                                placeholder={t('bolt_field_length')}
                                allowDecimals
                                value={editableData?.nominalLengthMm}
                                unit={t('common_unit_mm')}
                                disabled={!canWriteHardware || isArchived}
                                onChange={(event) =>
                                    changeData({
                                        nominalLengthMm: event.value ?? undefined,
                                    })
                                }
                            />
                            <Row gap={0.75}>
                                <DropdownField
                                    placeholder={t('bolt_field_material')}
                                    value={editableData?.material}
                                    options={MaterialSettings}
                                    disabled={!canWriteHardware || isArchived}
                                    onChange={(event) => changeData({ material: event.value })}
                                />

                                <DropdownField
                                    placeholder={t('bolt_field_diameter')}
                                    value={editableData?.diameter}
                                    options={BoltDiameterSettings}
                                    disabled={!canWriteHardware || isArchived}
                                    onChange={(event) => changeData({ diameter: event.value })}
                                />
                            </Row>
                            <DropdownField
                                placeholder={t('bolt_field_type')}
                                value={editableData?.type}
                                options={BoltTypeSettings}
                                disabled={!canWriteHardware || isArchived}
                                onChange={(event) => changeData({ type: event.value })}
                            />

                            <NumberField
                                placeholder={t('linear_force_constant')}
                                allowDecimals
                                value={editableData?.springConstantKnMm}
                                unit={t('common_unit_kn_mm')}
                                disabled={!canWriteHardware || isArchived}
                                onChange={(event) =>
                                    changeData({ springConstantKnMm: event.value })
                                }
                            />
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={1}>
                    <Card title={t('bolt_active_measurement_header')}>
                        <Column gap={2}>
                            {activeReference && (
                                <ActiveMeasurementSummary measurement={activeReference} />
                            )}
                            {latestElongation && (
                                <ActiveMeasurementSummary measurement={latestElongation} />
                            )}
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={1}>
                    <Card title={t('individual_pretension')}>
                        <BoundaryIndicator
                            value={toDecimalPlaces(selectedBolt.pretension, 1)}
                            min={selectedBoltParent?.pretensionForceMin ?? 0}
                            max={selectedBoltParent?.pretensionForceMax ?? 0}
                        />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
            <LayoutRowContainer>
                <MeasurementDetailsDialog
                    referenceMeasurement={selectedBolt.activeReference}
                    measurement={selectedMeasurement}
                    sensor={measurementSensor}
                    device={measurementDevice}
                    onExport={() => onExportMeasurement(selectedMeasurement)}
                    onHide={() => setSelectedMeasurement(undefined)}
                />
                <ContentColumn columns={3}>
                    <Card title={selectedBolt?.name}>
                        <CardContextMenu menu={menuItems} />
                        <CreateManualMeasurementDialog
                            bolt={selectedBolt}
                            visible={createManualVisible}
                            onHide={() => setCreateManualVisible(false)}
                            onSubmit={(data) => createManualMeasurement(selectedBolt, data)}
                        />
                        <MeasurementCommentDialog
                            visible={!!commentableMeasurement}
                            initialComment={commentableMeasurement?.comment}
                            onHide={() => setCommentableMeasurement(undefined)}
                            onSubmit={submitCommentChange}
                        />
                        {clonableMeasurement && (
                            <CreateManualMeasurementDialog
                                bolt={selectedBolt}
                                measurement={clonableMeasurement}
                                visible={!!clonableMeasurement}
                                onHide={() => setClonableMeasurement(undefined)}
                                onSubmit={(data) => createManualMeasurement(selectedBolt, data)}
                            />
                        )}
                        <ExpandableTable
                            value={measurements}
                            rowHover={true}
                            emptyMessage={t('measurements_empty')}
                            rowClassName={(m: Measurement) =>
                                m.isArchived ? 'archived-table-row' : undefined
                            }
                            onRowClick={(event) =>
                                setSelectedMeasurement(event.data as Measurement)
                            }
                            rowExpansionTemplate={(data: Measurement) => (
                                <ExpandedMeasurementRow
                                    measurement={data}
                                    onGraphClick={() => setSelectedMeasurement(data)}
                                />
                            )}
                        >
                            <TableColumn expander={true} />

                            <TableColumn
                                header={t('common_index')}
                                body={(_, o) => o.rowIndex + 1}
                            />

                            <TableColumn
                                header={t('measurement_field_measured')}
                                body={(m: Measurement) => (
                                    <Row gap={1} alignment="center">
                                        <TooltipAvatar
                                            id={m.measuredBy}
                                            target={m.id + m.measuredBy}
                                            position="bottom"
                                        />
                                        <DateDisplay value={m.measuredAt} target={m.id} />
                                    </Row>
                                )}
                            />

                            <TableColumn
                                header={t('measurement_field_type')}
                                body={(m: Measurement) => _ta(m, 'type')}
                            />

                            <TableColumn
                                header={t('measurement_field_measured_temperature')}
                                body={(m: Measurement) =>
                                    m.isManual ? (
                                        <PrimeIcon name="minus" />
                                    ) : (
                                        degreeC(m.reading.temperatureC)
                                    )
                                }
                            />

                            <TableColumn
                                header={t('measurement_field_result')}
                                body={(m: Measurement) => (
                                    <Row gap={1}>
                                        {m.normalized
                                            ? mm(toDecimalPlaces(m.normalized?.resultMm))
                                            : '-'}
                                        {m.isManual && <ManualMeasurementIndicator />}
                                    </Row>
                                )}
                            />

                            <TableColumn
                                header={t('measurement_field_comment')}
                                align="center"
                                body={(m: Measurement) =>
                                    m.comment ? (
                                        <TooltipWrapper target={m.id + m.comment} label={m.comment}>
                                            <PrimeIcon
                                                className="measurement-table__comment-state"
                                                name="comment"
                                            />
                                        </TooltipWrapper>
                                    ) : undefined
                                }
                            />

                            <TableColumn
                                body={(m: Measurement) => (
                                    <TableActionContainer>
                                        {canWriteHardware && !(m.isArchived && isArchived) && (
                                            <>
                                                <EditButton
                                                    target={m.id}
                                                    onClick={() => setCommentableMeasurement(m)}
                                                    icon={pi('comment')}
                                                    tooltip={t('bolt_comment_edit')}
                                                />
                                                <DuplicateButton
                                                    target={m.id}
                                                    onClick={() => setClonableMeasurement(m)}
                                                />
                                            </>
                                        )}
                                        {me?.isAdmin && (
                                            <ExportButton
                                                target={m.id}
                                                onClick={() => onExportMeasurement(m)}
                                            />
                                        )}
                                        {/* if */}
                                        {canWriteHardware && !(m.isArchived || isArchived) && (
                                            <ArchiveButton
                                                target={m.id}
                                                onClick={() =>
                                                    archiveMeasurement(selectedBolt.id, m.id)
                                                }
                                            />
                                        )}
                                        {/* else */}
                                        {canWriteHardware && (m.isArchived || isArchived) && (
                                            <RestoreButton
                                                target={m.id}
                                                onClick={() =>
                                                    restoreMeasurement(selectedBolt.id, m.id)
                                                }
                                            />
                                        )}
                                    </TableActionContainer>
                                )}
                            />
                        </ExpandableTable>
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </StructureScreen>
    )
}

interface ActiveMeasurementSummaryProps {
    measurement: Measurement
}
const ActiveMeasurementSummary = ({ measurement }: ActiveMeasurementSummaryProps) => {
    const result = measurement.normalized?.resultMm
    if (!result) return null
    return (
        <Column className="active-measurement-summary" gap={0.5} arrangement="spaced">
            <Row
                arrangement="spaced"
                alignment="center"
                className="active-measurement-summary__type"
            >
                {_ta(measurement, 'type')}
                {measurement.isManual && (
                    <div className="active-measurement-summary__tag">
                        <ManualMeasurementIndicator />
                    </div>
                )}
            </Row>
            <Row className="active-measurement-summary__value" gap={0.25} alignment="end">
                {toDecimalPlaces(result)}
                <div className="active-measurement-summary__unit">{t('common_unit_mm')}</div>
            </Row>
        </Column>
    )
}

const ManualMeasurementIndicator = () => (
    <Tag className="tag" value={t('common_manual')} severity="secondary" icon={pi('info-circle')} />
)

interface MeasurementRowExpansion {
    measurement: Measurement
    onGraphClick?: () => void
}
const ExpandedMeasurementRow = ({ measurement, onGraphClick }: MeasurementRowExpansion) => {
    const classNames = new ClassNameBuilder()
        .add('measurement-table')
        .addIf('archived-table-row', measurement.isArchived)
    return (
        <DataTable className={classNames.toString()} unstyled value={[measurement]}>
            <TableColumn
                header={t('measurement_field_waveform')}
                body={(m: Measurement) => (
                    <WaveGraph
                        primaryCoordinates={m.coordinates}
                        width={200}
                        height={48}
                        onClick={onGraphClick}
                    />
                )}
            />

            <TableColumn
                header={t('measurement_field_polarity')}
                body={(m: Measurement) => _ta(m, 'polarity')}
            />

            <TableColumn
                header={t('measurement_field_delay')}
                body={(m: Measurement) => mm(m.settings.delayMm)}
            />
            <TableColumn
                header={t('measurement_field_gain')}
                body={(m: Measurement) => m.settings.gain}
            />
            <TableColumn
                header={t('measurement_field_gate')}
                body={(m: Measurement) => mm(m.settings.gateMm)}
            />
            <TableColumn
                header={t('measurement_field_threshold')}
                body={(m: Measurement) => m.settings.threshold}
            />
            <TableColumn
                header={t('measurement_field_range')}
                body={(m: Measurement) => mm(m.settings.rangeMm)}
            />
        </DataTable>
    )
}
